import {
  NotificationDto,
  NotificationType,
  RelatedEntityType,
} from '../api/types/notification.types';

/**
 * Icon mapping for notification types
 */
export const notificationIcons: Record<NotificationType, string> = {
  [NotificationType.BUY_BOX_MATCH]: '🏠',
  [NotificationType.NEW_BID]: '💰',
  [NotificationType.BID_ACCEPTED]: '✅',
  [NotificationType.BID_DECLINED]: '❌',
  [NotificationType.BID_COUNTERED]: '🔄',
  [NotificationType.BID_MESSAGE]: '💬',
  [NotificationType.BID_WON]: '🎉',
  [NotificationType.BID_BACKUP]: '⏳',
  [NotificationType.CONNECTION_REQUEST]: '👤',
  [NotificationType.CONNECTION_ACCEPTED]: '🤝',
  [NotificationType.LISTING_TAG]: '🏷️',
  [NotificationType.LISTING_BACK_ON_MARKET]: '🔄',
  [NotificationType.ENTITY_VERIFIED]: '✅',
  [NotificationType.VERIFICATION_REJECTED]: '❌',
  [NotificationType.DOCUMENT_APPROVED]: '✅',
  [NotificationType.DOCUMENT_REJECTED]: '❌',
  [NotificationType.TIER_UPGRADED]: '🏆',
  // Legacy types
  [NotificationType.BID_STATUS_CHANGE]: '📊',
  [NotificationType.NEW_MESSAGE]: '💬',
};

/**
 * Get the icon for a notification type
 */
export function getNotificationIcon(type: NotificationType): string {
  return notificationIcons[type] || '🔔';
}

/**
 * Get the navigation destination for a notification
 */
export function getNotificationDestination(notification: NotificationDto): string {
  const { type, relatedEntityType, relatedEntityId, data } = notification;

  switch (type) {
    case NotificationType.BUY_BOX_MATCH:
      if (relatedEntityType === RelatedEntityType.LISTING && relatedEntityId) {
        return `/buy/${relatedEntityId}`;
      }
      break;

    case NotificationType.NEW_BID:
      if (relatedEntityType === RelatedEntityType.BID && data) {
        const listingId = data.listingId as string;
        if (listingId && relatedEntityId) {
          // Navigate seller to the bid detail page
          return `/sell/listing/${listingId}/bid/${relatedEntityId}`;
        }
      }
      break;

    // Outcomes of the user's own bid (buyer side): open that bid on My bids
    case NotificationType.BID_STATUS_CHANGE:
    case NotificationType.BID_ACCEPTED:
    case NotificationType.BID_DECLINED:
    case NotificationType.BID_COUNTERED:
    case NotificationType.BID_WON: {
      const bidId =
        relatedEntityType === RelatedEntityType.BID && relatedEntityId
          ? relatedEntityId
          : typeof data?.bidId === 'string'
            ? data.bidId
            : null;
      if (bidId) return `/buy/bids/${bidId}`;
      break;
    }

    // Bid messages can reach either party; the inbox holds both sides' threads
    case NotificationType.BID_MESSAGE:
    case NotificationType.NEW_MESSAGE: {
      const bidId = typeof data?.bidId === 'string' ? data.bidId : null;
      if (bidId) return `/user/inbox/${bidId}`;
      return '/user/inbox';
    }

    case NotificationType.LISTING_BACK_ON_MARKET:
      if (relatedEntityType === RelatedEntityType.LISTING && relatedEntityId) {
        return `/buy/${relatedEntityId}`;
      }
      break;

    case NotificationType.ENTITY_VERIFIED:
      return '/profile';

    case NotificationType.VERIFICATION_REJECTED:
    case NotificationType.DOCUMENT_REJECTED:
    case NotificationType.DOCUMENT_APPROVED:
      return '/profile/verification';

    case NotificationType.TIER_UPGRADED:
      return '/profile';

    case NotificationType.CONNECTION_REQUEST:
    case NotificationType.CONNECTION_ACCEPTED:
      return '/network';

    case NotificationType.LISTING_TAG:
      if (typeof data?.listingId === 'string' && data.listingId) return `/buy/${data.listingId}`;
      if (relatedEntityType === RelatedEntityType.LISTING && relatedEntityId) return `/buy/${relatedEntityId}`;
      break;
  }

  // PRD 3.4: a price change opens the listing the buyer bid on; 3.3: the inviter's report opens their network
  if ((type as string) === 'LISTING_PRICE_CHANGED') {
    const listingId = listingIdOf(notification);
    if (listingId) return `/buy/${listingId}`;
  }
  if ((type as string) === 'LISTING_OFFERS_REPORT') return '/network';

  // MKT-43: the inviting company's instant offer opens on the seller's offer page
  if ((type as string) === 'FIRST_LOOK_OFFER') {
    const listingId = listingIdOf(notification);
    if (listingId && relatedEntityId) return `/sell/listing/${listingId}/bid/${relatedEntityId}`;
  }

  // Expiry reminders go to the seller's own listing (the types aren't in the client enum)
  if (LISTING_LIFECYCLE_TYPES.includes(type as string)) {
    const listingId = listingIdOf(notification);
    if (listingId) return `/sell/${listingId}`;
  }

  // PRD 8.1 AC3: anything else about a property opens the property
  const listingId = listingIdOf(notification);
  if (listingId) return `/buy/${listingId}`;

  // Default to notifications page
  return '/user/notifications';
}

/** Seller-side listing lifecycle notifications sent by the expiration job */
export const LISTING_LIFECYCLE_TYPES: string[] = [
  'LISTING_EXPIRING_SOON',
  'LISTING_EXPIRED',
  'FIRST_LOOK_STARTED',
  'FIRST_LOOK_RELEASED',
];

const textOf = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

/** The listing a notification is about: `data.listingId`, else a LISTING related entity */
export function listingIdOf(notification: Pick<NotificationDto, 'relatedEntityType' | 'relatedEntityId' | 'data'>): string | null {
  return (
    textOf(notification.data?.listingId) ??
    (notification.relatedEntityType === RelatedEntityType.LISTING ? textOf(notification.relatedEntityId) : null)
  );
}

export interface NotificationAction {
  label: string;
  to: string;
}

/**
 * The one follow-up a notification asks for, shown as a small inline button:
 * review a new bid, respond to a counter, or open a listing that is about to expire.
 * Null when the notification needs no action or the payload lacks the ids.
 */
export function getNotificationAction(notification: NotificationDto): NotificationAction | null {
  const { type, relatedEntityType, relatedEntityId, data } = notification;
  switch (type) {
    case NotificationType.NEW_BID: {
      const listingId = textOf(data?.listingId);
      return listingId ? { label: 'Review', to: `/sell/offers/${listingId}` } : null;
    }
    case NotificationType.BID_COUNTERED: {
      const bidId =
        relatedEntityType === RelatedEntityType.BID && textOf(relatedEntityId) ? relatedEntityId : textOf(data?.bidId);
      return bidId ? { label: 'Respond', to: `/buy/bids/${bidId}` } : null;
    }
  }
  if (LISTING_LIFECYCLE_TYPES.includes(type as string)) {
    const listingId = listingIdOf(notification);
    return listingId ? { label: 'View listing', to: `/sell/${listingId}` } : null;
  }
  return null;
}

/**
 * Format relative timestamp
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else {
    // Format as date for older notifications
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }
}

/**
 * Format absolute timestamp for hover tooltip
 */
export function formatAbsoluteTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format unread count for badge display
 */
export function formatUnreadCount(count: number): string {
  if (count === 0) {
    return '';
  } else if (count > 99) {
    return '99+';
  }
  return count.toString();
}

/**
 * Filter label mapping (sentence case, short enough for tabs)
 */
export const notificationFilterLabels: Record<string, string> = {
  all: 'All',
  [NotificationType.BUY_BOX_MATCH]: 'Matches',
  bids: 'Bids & offers',
  [NotificationType.NEW_MESSAGE]: 'Messages',
  verification: 'Verification',
};

/** Bid activity, excluding messages (they have their own filter) */
export const BID_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.NEW_BID,
  NotificationType.BID_ACCEPTED,
  NotificationType.BID_DECLINED,
  NotificationType.BID_COUNTERED,
  NotificationType.BID_WON,
  NotificationType.BID_STATUS_CHANGE,
];

/** Bid thread messages plus the legacy message type */
export const MESSAGE_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.BID_MESSAGE,
  NotificationType.NEW_MESSAGE,
];

export const VERIFICATION_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.ENTITY_VERIFIED,
  NotificationType.VERIFICATION_REJECTED,
  NotificationType.DOCUMENT_APPROVED,
  NotificationType.DOCUMENT_REJECTED,
  NotificationType.TIER_UPGRADED,
];

/**
 * Get notification types for a filter category (undefined = no type filter)
 */
export function getTypesForFilter(filter: string): NotificationType[] | undefined {
  switch (filter) {
    case 'all':
      return undefined;
    case NotificationType.BUY_BOX_MATCH:
      return [NotificationType.BUY_BOX_MATCH];
    case 'bids':
      return BID_NOTIFICATION_TYPES;
    case NotificationType.NEW_MESSAGE:
      return MESSAGE_NOTIFICATION_TYPES;
    case 'verification':
      return VERIFICATION_NOTIFICATION_TYPES;
    default:
      return undefined;
  }
}
